import { useState, useCallback, useRef, useEffect } from 'react';
import {
  AiProfileField,
  AiTone,
  AiStyle,
  GeneratedOption,
  useGenerateProfileTextModel,
} from '../model/GenerateProfileText.model';

export type { AiProfileField } from '../model/GenerateProfileText.model';

type AiAssistForm = {
  tone: AiTone;
  style: AiStyle;
  thingsToMention: string;
};

type GenerationState =
  | { type: 'not-fetched' }
  | { type: 'fetching' }
  | {
      type: 'fetched';
      value: {
        options: ReadonlyArray<GeneratedOption>;
        remainingGenerations: number;
      };
    }
  | { type: 'fetch-error'; value: string };

type ProfileFormField = 'messageProfile' | 'accomplishments';

type AcceptResult = {
  formField: ProfileFormField;
  text: string;
};

type UndoState = {
  formField: ProfileFormField;
  previousText: string;
} | null;

export type AiAssistVM = {
  isOpen: boolean;
  field: AiProfileField | null;
  generation: GenerationState;
  selectedIndex: number;
  form: AiAssistForm;
  remainingGenerations: number | null;
  currentOption: GeneratedOption | null;
  totalOptions: number;
  undoAvailable: UndoState;
  open: (field: AiProfileField, currentText: string) => void;
  close: () => void;
  updateForm: (updates: Partial<AiAssistForm>) => void;
  generate: () => void;
  nextOption: () => void;
  prevOption: () => void;
  accept: () => AcceptResult | null;
  undo: () => UndoState;
};

const DEFAULT_FORM: AiAssistForm = {
  tone: 'WARM_AND_APPROACHABLE',
  style: 'MEDIUM',
  thingsToMention: '',
};

export const THINGS_TO_MENTION_MAX = 200;

const UNDO_TIMEOUT_MS = 8000;

const aiFieldToFormField = (field: AiProfileField): ProfileFormField =>
  field === 'BIO' ? 'messageProfile' : 'accomplishments';

export const useAiAssistVM = (creatorId: number): AiAssistVM => {
  const { generate: generateText } = useGenerateProfileTextModel(creatorId);

  const [isOpen, setIsOpen] = useState(false);
  const [field, setField] = useState<AiProfileField | null>(null);
  const [generation, setGeneration] = useState<GenerationState>({
    type: 'not-fetched',
  });
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [form, setForm] = useState<AiAssistForm>(DEFAULT_FORM);
  const [remainingGenerations, setRemainingGenerations] = useState<
    number | null
  >(null);
  const [undoAvailable, setUndoAvailable] = useState<UndoState>(null);

  const currentTextRef = useRef('');
  const undoTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
    };
  }, []);

  const open = useCallback(
    (targetField: AiProfileField, currentText: string) => {
      setIsOpen(true);
      setField(targetField);
      setGeneration({ type: 'not-fetched' });
      setSelectedIndex(0);
      setForm(DEFAULT_FORM);
      currentTextRef.current = currentText;
    },
    [],
  );

  const close = useCallback(() => {
    setIsOpen(false);
    setField(null);
    setGeneration({ type: 'not-fetched' });
    setSelectedIndex(0);
  }, []);

  const updateForm = useCallback((updates: Partial<AiAssistForm>) => {
    setForm((prev) => ({
      ...prev,
      ...updates,
      thingsToMention:
        updates.thingsToMention !== undefined
          ? updates.thingsToMention.slice(0, THINGS_TO_MENTION_MAX)
          : prev.thingsToMention,
    }));
  }, []);

  const generate = useCallback(() => {
    if (!field) return;
    if (generation.type === 'fetching') return;

    setGeneration({ type: 'fetching' });
    setSelectedIndex(0);

    generateText({
      field,
      tone: form.tone,
      style: form.style,
      thingsToMention: form.thingsToMention || undefined,
      initialInput: currentTextRef.current || undefined,
    })
      .then((result) => {
        setGeneration({ type: 'fetched', value: result });
        setRemainingGenerations(result.remainingGenerations);
      })
      .catch((err) => {
        const message =
          err instanceof Error ? err.message : 'Failed to generate text';
        setGeneration({ type: 'fetch-error', value: message });
      });
  }, [field, generation.type, form, generateText]);

  const nextOption = useCallback(() => {
    if (generation.type !== 'fetched') return;
    const total = generation.value.options.length;
    setSelectedIndex((prev) => (prev + 1) % total);
  }, [generation]);

  const prevOption = useCallback(() => {
    if (generation.type !== 'fetched') return;
    const total = generation.value.options.length;
    setSelectedIndex((prev) => (prev - 1 + total) % total);
  }, [generation]);

  const accept = useCallback((): AcceptResult | null => {
    if (generation.type !== 'fetched') return null;
    if (!field) return null;
    const option = generation.value.options[selectedIndex];
    if (!option) return null;

    const formField = aiFieldToFormField(field);

    if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
    setUndoAvailable({ formField, previousText: currentTextRef.current });
    undoTimerRef.current = setTimeout(() => {
      setUndoAvailable(null);
    }, UNDO_TIMEOUT_MS);

    currentTextRef.current = option.text;
    close();

    return { formField, text: option.text };
  }, [generation, selectedIndex, field, close]);

  const undo = useCallback((): UndoState => {
    const current = undoAvailable;
    if (!current) return null;

    if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
    setUndoAvailable(null);

    return current;
  }, [undoAvailable]);

  const currentOption =
    generation.type === 'fetched'
      ? (generation.value.options[selectedIndex] ?? null)
      : null;

  const totalOptions =
    generation.type === 'fetched' ? generation.value.options.length : 0;

  return {
    isOpen,
    field,
    generation,
    selectedIndex,
    form,
    remainingGenerations,
    currentOption,
    totalOptions,
    undoAvailable,
    open,
    close,
    updateForm,
    generate,
    nextOption,
    prevOption,
    accept,
    undo,
  };
};
